import { useState, useEffect } from 'react';
import { api } from '../lib/api';

export interface ThemeColors {
  primary: string | null;
  secondary: string | null;
  accent: string | null;
}

export interface ThemeFonts {
  heading: string | null;
  body: string | null;
}

export interface ThemePreview {
  name: string;
  description: string;
  thumbnail: string;
  has_branding: boolean;
}

export interface Theme {
  id: number;
  key: string;
  name: string;
  version: string;
  manifest: {
    description?: string;
    settings?: {
      colors?: Record<string, string>;
      fonts?: Record<string, string>;
    };
    templates?: Record<string, any>;
    sections?: Record<string, any>;
  };
  preview: ThemePreview;
  is_active: boolean;
}

export interface OrganizationBranding {
  name: string;
  has_branding: boolean;
  brand_colors: ThemeColors;
  brand_fonts: ThemeFonts;
}

export interface ThemesWithBranding {
  themes: Theme[];
  organization: OrganizationBranding;
}

export const useCmsThemes = () => {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [organization, setOrganization] = useState<OrganizationBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch themes with organization branding applied
        const response = await api.get('/api/cms/themes/with_branding/');
        const data = response.data as ThemesWithBranding;
        
        setThemes(data.themes);
        setOrganization(data.organization);
      } catch (err) {
        setError('Failed to load themes. Please try again.');
        
        // Fallback to regular themes endpoint if branded endpoint fails
        try {
          const fallbackResponse = await api.get('/api/cms/themes/');
          const fallbackThemes = fallbackResponse.data as Theme[];
          
          // Add basic preview info to fallback themes
          const themesWithPreview = fallbackThemes.map(theme => ({
            ...theme,
            preview: {
              name: theme.name,
              description: theme.manifest?.description || '',
              thumbnail: `/static/cms/themes/${theme.key}/preview.jpg`,
              has_branding: false
            }
          }));
          
          setThemes(themesWithPreview);
        } catch {
          // Intentionally empty
        }
      } finally {
        setLoading(false);
      }
    };

    fetchThemes();
  }, []);

  const getThemeById = (themeId: string) => {
    return themes.find(theme => theme.key === themeId);
  };

  const getThemeColors = (theme: Theme): ThemeColors => {
    const colors = theme.manifest?.settings?.colors || {};
    return {
      primary: colors.primary || null,
      secondary: colors.secondary || null,
      accent: colors.accent || null,
    };
  };

  const getThemeFonts = (theme: Theme): ThemeFonts => {
    const fonts = theme.manifest?.settings?.fonts || {};
    return {
      heading: fonts.heading || fonts.fallback_heading || null,
      body: fonts.body || fonts.fallback_body || null,
    };
  };

  const getThemePreviewStyle = (theme: Theme) => {
    const colors = getThemeColors(theme);
    const fonts = getThemeFonts(theme);
    
    return {
      '--theme-primary': colors.primary || '#2563eb',
      '--theme-secondary': colors.secondary || '#1e40af',
      '--theme-accent': colors.accent || '#f59e0b',
      '--theme-font-heading': fonts.heading || 'Inter, sans-serif',
      '--theme-font-body': fonts.body || 'Inter, sans-serif',
    } as React.CSSProperties;
  };

  return {
    themes,
    organization,
    loading,
    error,
    getThemeById,
    getThemeColors,
    getThemeFonts,
    getThemePreviewStyle,
  };
};